import { formatNumber } from '@/utils/value-format';

const SECTORS = 4;
const GAP_DEG = 4;

const getHexColor = (value: number | undefined) => {
  return value === undefined || value === 0
    ? 'hsl(var(--vestgrey-100))'
    : value >= 60
      ? 'hsl(var(--green))'
      : value > 20
        ? 'hsl(var(--yellow))'
        : 'hsl(var(--red))';
};

const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => {
  const rad = ((angle - 90) * Math.PI) / 180;

  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
};

const describeArc = (
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;

  return `
    M ${start.x} ${start.y}
    A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}
  `;
};

interface HealthCircleProps {
  value?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

const HealthCircle = (props: HealthCircleProps) => {
  const { value = 0, size = 74, strokeWidth = 4, color } = props;

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth) / 2;
  const normalized = Math.min(Math.max(value, 0), 100);
  const totalDeg = (normalized / 100) * 360;
  const sectorDeg = 360 / SECTORS;

  return (
    <svg width={size} height={size}>
      {Array.from({ length: SECTORS }).map((_, i) => {
        const start = i * sectorDeg + GAP_DEG / 2;
        const end = (i + 1) * sectorDeg - GAP_DEG / 2;

        return (
          <path
            key={`bg-${i}`}
            d={describeArc(cx, cy, r, start, end)}
            stroke="hsl(var(--border))"
            fill="none"
            strokeWidth={strokeWidth}
          />
        );
      })}

      {Array.from({ length: SECTORS }).map((_, i) => {
        const sectorStart = i * sectorDeg;
        const sectorEnd = sectorStart + sectorDeg;

        if (totalDeg <= sectorStart) {
          return null;
        }

        const filled = Math.min(totalDeg, sectorEnd) - sectorStart;
        const start = sectorStart + GAP_DEG / 2;
        const end = sectorStart + filled - GAP_DEG / 2;

        if (end <= start) {
          return null;
        }

        return (
          <path
            key={`fg-${i}`}
            d={describeArc(cx, cy, r, start, end)}
            fill="none"
            stroke={color || getHexColor(value)}
            strokeWidth={strokeWidth}
          />
        );
      })}

      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={18}
        fontWeight={500}
        fill={color || getHexColor(value)}
      >
        {formatNumber(normalized, { maxDigits: 1, style: 'percent' })}
      </text>
    </svg>
  );
};

export default HealthCircle;
